<template>
  <v-dialog :model-value="show" persistent max-width="620">
    <v-card color="surface" rounded="lg" class="pa-6">
      <div class="text-center mb-5">
        <v-avatar color="primary" variant="tonal" rounded="lg" size="52" class="mb-3">
          <v-icon icon="mdi-rocket-launch-outline" size="28" />
        </v-avatar>
        <h2 class="text-h5 font-weight-bold mb-2">There’s a simpler way to work now</h2>
        <p class="text-body-2 text-medium-emphasis mb-0">
          We’ve added a streamlined <strong>Simple mode</strong> — a friendlier workspace built on
          everything you already have. Your agents, workflows and schedules are all still here.
        </p>
      </div>

      <v-list class="bg-transparent mb-2" density="comfortable">
        <v-list-item v-for="item in highlights" :key="item.title" class="px-0">
          <template #prepend>
            <v-icon :icon="item.icon" color="primary" class="mr-3" />
          </template>
          <v-list-item-title class="font-weight-medium">{{ item.title }}</v-list-item-title>
          <v-list-item-subtitle class="text-wrap">{{ item.desc }}</v-list-item-subtitle>
        </v-list-item>
      </v-list>

      <p class="text-caption text-medium-emphasis text-center mb-5">
        You can switch back to Advanced anytime from the menu.
      </p>

      <div class="d-flex flex-column flex-sm-row ga-3 justify-center">
        <v-btn color="primary" variant="flat" size="large" @click="trySimple">
          Try Simple mode
        </v-btn>
        <v-btn variant="text" size="large" @click="dismiss">Maybe later</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { mapGetters } from 'vuex'
import { getDashboardTabPath } from '@/router/dashboardTabs'

@Options({
  props: {
    // DashboardView snapshots `shouldAnnounceSimpleUi` once the initial data fetch resolves and passes
    // it here. Snapshotting (rather than binding the live getter) keeps the popup a load-time decision,
    // so an advanced user crossing 2 agents mid-session can't trigger it.
    eligible: { type: Boolean, default: false }
  },
  computed: {
    ...mapGetters(['authenticated', 'simpleUiAnnouncementSeen'])
  }
})
export default class SimpleUiAnnouncementDialog extends Vue {
  eligible!: boolean
  authenticated!: boolean | null
  simpleUiAnnouncementSeen!: boolean

  highlights = [
    {
      icon: 'mdi-account-heart-outline',
      title: 'Assistants',
      desc: 'Create and tune AI helpers in one place — model, instructions, tools and voice.'
    },
    {
      icon: 'mdi-cog-sync-outline',
      title: 'Automations',
      desc: 'Teams, routines and timed tasks under one simple, plain-language editor.'
    },
    {
      icon: 'mdi-message-text-outline',
      title: 'Describe it in chat',
      desc: 'Tell the assistant what you want in plain words and it sets things up for you.'
    }
  ]

  // Only ever shown to a signed-in, eligible (snapshotted) user who hasn't dismissed it yet. Checking
  // `simpleUiAnnouncementSeen` reactively here closes the dialog the instant either button marks it seen.
  get show(): boolean {
    return this.authenticated === true && this.eligible && !this.simpleUiAnnouncementSeen
  }

  // Mark seen first so the dialog closes regardless of where navigation lands the user.
  trySimple() {
    this.$store.dispatch('setSimpleUiAnnouncementSeen', true)
    this.$store.dispatch('setUserMode', 'simple')
    const assistantsPath = getDashboardTabPath('assistants')
    if (this.$route.path !== assistantsPath) {
      void this.$router.push(assistantsPath)
    }
  }

  dismiss() {
    this.$store.dispatch('setSimpleUiAnnouncementSeen', true)
  }
}
</script>
